/**
 * Cards, suits, and the trick-taking rules that depend only on cards and trump.
 *
 * Cards are short strings so they serialize cleanly over the wire:
 *   rank + suit, e.g. "9C", "10H", "JS", "AD".
 */

export const SUITS = ['C', 'D', 'H', 'S'] as const;
export type Suit = (typeof SUITS)[number];

export const RANKS = ['9', '10', 'J', 'Q', 'K', 'A'] as const;
export type Rank = (typeof RANKS)[number];

export type Card = `${Rank}${Suit}`;

export type Seat = 0 | 1 | 2 | 3;
export const SEATS: readonly Seat[] = [0, 1, 2, 3];

/** Seats 0 & 2 are team 0; seats 1 & 3 are team 1. */
export type Team = 0 | 1;
export const teamOf = (seat: Seat): Team => (seat % 2) as Team;
export const partnerOf = (seat: Seat): Seat => ((seat + 2) % 4) as Seat;
export const leftOf = (seat: Seat): Seat => ((seat + 1) % 4) as Seat;

export const SUIT_NAMES: Record<Suit, string> = { C: 'Clubs', D: 'Diamonds', H: 'Hearts', S: 'Spades' };
/** Text-style variation selector: keeps ♥ ♦ from rendering as colour emoji. */
const TEXT_STYLE = String.fromCodePoint(0xfe0e);
export const SUIT_SYMBOLS: Record<Suit, string> = { C: '♣' + TEXT_STYLE, D: '♦' + TEXT_STYLE, H: '♥' + TEXT_STYLE, S: '♠' + TEXT_STYLE };
export const RANK_NAMES: Record<Rank, string> = { '9': 'Nine', '10': 'Ten', J: 'Jack', Q: 'Queen', K: 'King', A: 'Ace' };

/** The other suit of the same colour (clubs <-> spades, diamonds <-> hearts). */
export const SAME_COLOR: Record<Suit, Suit> = { C: 'S', S: 'C', D: 'H', H: 'D' };

export function makeCard(rank: Rank, suit: Suit): Card {
  return `${rank}${suit}` as Card;
}

export function rankOf(card: Card): Rank {
  return card.slice(0, -1) as Rank;
}

export function printedSuitOf(card: Card): Suit {
  return card.slice(-1) as Suit;
}

const CARD_SET: ReadonlySet<string> = new Set(SUITS.flatMap((s) => RANKS.map((r) => makeCard(r, s))));

export function isCard(value: unknown): value is Card {
  return typeof value === 'string' && CARD_SET.has(value);
}

export function isSuit(value: unknown): value is Suit {
  return typeof value === 'string' && (SUITS as readonly string[]).includes(value);
}

/** A fresh, ordered 24-card euchre deck (9 through Ace in each suit). */
export function newDeck(): Card[] {
  return SUITS.flatMap((s) => RANKS.map((r) => makeCard(r, s)));
}

export function isRightBower(card: Card, trump: Suit): boolean {
  return card === makeCard('J', trump);
}

export function isLeftBower(card: Card, trump: Suit): boolean {
  return card === makeCard('J', SAME_COLOR[trump]);
}

/**
 * The suit a card belongs to for following-suit purposes once trump is known.
 * The left bower is a trump card, not a card of its printed suit.
 */
export function effectiveSuit(card: Card, trump: Suit): Suit {
  return isLeftBower(card, trump) ? trump : printedSuitOf(card);
}

export function isTrump(card: Card, trump: Suit): boolean {
  return effectiveSuit(card, trump) === trump;
}

const PLAIN_ORDER: Record<Rank, number> = { '9': 1, '10': 2, J: 3, Q: 4, K: 5, A: 6 };
const TRUMP_ORDER: Record<Rank, number> = { '9': 1, '10': 2, J: 0 /* bowers handled separately */, Q: 3, K: 4, A: 5 };

/**
 * Strength of a card within a single trick.
 * Trump cards: 101..108 (right bower 108, left bower 107, A 105 ... 9 101).
 * Cards of the led suit: 1..6. Anything else: 0 (cannot win).
 */
export function trickStrength(card: Card, trump: Suit, ledSuit: Suit): number {
  if (isRightBower(card, trump)) return 108;
  if (isLeftBower(card, trump)) return 107;
  const suit = printedSuitOf(card);
  if (suit === trump) return 100 + TRUMP_ORDER[rankOf(card)];
  if (suit === ledSuit) return PLAIN_ORDER[rankOf(card)];
  return 0;
}

/**
 * Card power independent of what was led: trump outranks everything, otherwise
 * plain rank. Useful for ordering a hand and for "lowest card" choices.
 */
export function cardPower(card: Card, trump: Suit | null): number {
  if (trump !== null) {
    if (isRightBower(card, trump)) return 108;
    if (isLeftBower(card, trump)) return 107;
    if (printedSuitOf(card) === trump) return 100 + TRUMP_ORDER[rankOf(card)];
  }
  return PLAIN_ORDER[rankOf(card)];
}

export interface PlayedCard {
  seat: Seat;
  card: Card;
}

/** Suit led in a trick (effective suit of the first card). */
export function ledSuitOf(trick: readonly PlayedCard[], trump: Suit): Suit | null {
  const first = trick[0];
  return first ? effectiveSuit(first.card, trump) : null;
}

/** Which seat is currently winning (or has won) the given trick. */
export function currentWinner(trick: readonly PlayedCard[], trump: Suit): Seat | null {
  const led = ledSuitOf(trick, trump);
  if (led === null) return null;
  let best: PlayedCard | null = null;
  let bestStrength = -1;
  for (const pc of trick) {
    const s = trickStrength(pc.card, trump, led);
    if (s > bestStrength) {
      best = pc;
      bestStrength = s;
    }
  }
  return best ? best.seat : null;
}

/**
 * Cards from `hand` that may legally be played to `trick`.
 * You must follow the led (effective) suit if you can; otherwise anything goes.
 */
export function legalPlays(hand: readonly Card[], trick: readonly PlayedCard[], trump: Suit): Card[] {
  const led = ledSuitOf(trick, trump);
  if (led === null) return [...hand];
  const following = hand.filter((c) => effectiveSuit(c, trump) === led);
  return following.length > 0 ? following : [...hand];
}

/** Sort a hand for display: trump first (high to low), then other suits grouped, alternating colours. */
export function sortHand(hand: readonly Card[], trump: Suit | null): Card[] {
  const suitOrder: Suit[] = trump
    ? [trump, ...(['S', 'H', 'C', 'D'] as Suit[]).filter((s) => s !== trump)]
    : ['S', 'H', 'C', 'D'];
  // Keep colours alternating where possible for readability.
  const ordered = alternateColours(suitOrder);
  const groupIndex = (c: Card) => ordered.indexOf(trump ? effectiveSuit(c, trump) : printedSuitOf(c));
  return [...hand].sort((a, b) => groupIndex(a) - groupIndex(b) || cardPower(b, trump) - cardPower(a, trump));
}

function alternateColours(order: Suit[]): Suit[] {
  const [first, ...rest] = order;
  if (!first) return order;
  const out: Suit[] = [first];
  const red = (s: Suit) => s === 'H' || s === 'D';
  const pool = [...rest];
  while (pool.length) {
    const last = out[out.length - 1]!;
    const idx = pool.findIndex((s) => red(s) !== red(last));
    out.push(pool.splice(idx >= 0 ? idx : 0, 1)[0]!);
  }
  return out;
}

export function cardLabel(card: Card): string {
  return `${rankOf(card)}${SUIT_SYMBOLS[printedSuitOf(card)]}`;
}
